import { Document, Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer'
import { styles } from '../styles'
import { SECTIONS, COLORS } from '../constants'
import { SectionHeader } from '../components/SectionHeader'
import { LimitationsBox } from '../components/LimitationsBox'
import { CategoryBlock } from '../components/CategoryBlock'
import { PageFooter } from '../components/PageFooter'
import { RatingCircle } from '../components/RatingCircle'

// Header templates for each section (add more as created)
const SECTION_HEADERS = {
  D: '/D_header.png?v=1',
  E: '/E_header.png?v=3',
  F: '/F_header.png?v=1',
  G: '/G_header.png?v=1'
}

// Styles for pages with header templates
const templateStyles = StyleSheet.create({
  pageWithHeader: {
    fontFamily: 'Helvetica',
    fontSize: 10,
    lineHeight: 1.4,
    color: '#000',
    paddingTop: 110,
    paddingLeft: 40,
    paddingRight: 40,
    paddingBottom: 70
  },
  headerImage: {
    width: 595,
    marginLeft: -40,
    marginRight: -40,
    marginTop: -110
  },
  continuationHeader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0
  },
  continuationLogo: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 595
  },
  contentAfterHeader: {
  },
  limitationsBoxOnly: {
    marginLeft: 25,
    marginTop: 2,
    marginBottom: 15
  },
  categoryWrapper: {
    marginLeft: -40
  },
  limitationsInnerBox: {
    borderWidth: 1,
    borderColor: '#999',
    borderRadius: 4,
    minHeight: 20,
    padding: 6
  },
  limitationsText: {
    fontSize: 9
  },
  ratingLegend: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    marginBottom: 15,
    marginRight: 0,
    alignItems: 'center'
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 40,
    right: 40,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 8,
    color: '#000'
  },
  footerText: {
    position: 'absolute',
    bottom: 15,
    left: 75,
    fontSize: 10,
    color: '#808080',
    fontFamily: 'Helvetica'
  },
  footerImage: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: 595,
    height: 40
  }
})

export function SurveyDocument({ survey, photos, categoryData, customCategories = [] }) {
  const surveyLevel = survey?.surveyLevel || 1

  // Helper to get photos for a category
  const getPhotosForCategory = (code) => {
    return photos?.filter(p => p.categoryCode === code) || []
  }

  // Helper to get category data
  const getCategoryData = (code) => {
    return categoryData?.[code] || {}
  }

  // Helper to get limitations text for a section
  const getLimitations = (sectionLetter) => {
    const key = `${sectionLetter}_limitations`
    return categoryData?.[key]?.comment || ''
  }

  // Get custom categories for a specific section
  const getCustomCategoriesForSection = (sectionLetter) => {
    return customCategories.filter(c => c.section === sectionLetter)
  }

  // Get standalone custom categories (no section assigned)
  const standaloneCategories = customCategories.filter(c => !c.section)

  return (
    <Document>
      {Object.entries(SECTIONS).map(([sectionLetter, section]) => {
        const hasHeader = SECTION_HEADERS[sectionLetter]

        if (hasHeader) {
          // Use header image template for this section
          return (
            <Page key={sectionLetter} size="A4" style={templateStyles.pageWithHeader}>
              {/* Fixed RICS header for continuation pages */}
              <Image
                src="/RICS.png?v=3"
                style={templateStyles.continuationLogo}
                fixed
              />

              {/* Header image - flows as normal content (covers continuation header on page 1) */}
              <Image src={hasHeader} style={templateStyles.headerImage} />

              {/* Content after header with padding */}
              <View style={templateStyles.contentAfterHeader}>
                {/* Limitations box (just the box, label is in header image) */}
                <View style={templateStyles.limitationsBoxOnly}>
                  <View style={templateStyles.limitationsInnerBox}>
                    <Text style={templateStyles.limitationsText}>
                      {getLimitations(sectionLetter) || ''}
                    </Text>
                  </View>
                </View>

                {/* Rating legend */}
                <View style={templateStyles.ratingLegend}>
                  <RatingCircle rating="1" size={20} />
                  <RatingCircle rating="2" size={20} />
                  <RatingCircle rating="3" size={20} />
                  <RatingCircle rating="NI" size={20} />
                </View>

                {/* Categories */}
                <View style={templateStyles.categoryWrapper}>
                  {Object.entries(section.categories).map(([code, name]) => {
                    const catData = getCategoryData(code)
                    const catPhotos = getPhotosForCategory(code)

                    return (
                      <CategoryBlock
                        key={code}
                        code={code}
                        name={name}
                        comment={catData.comment}
                        rating={catData.rating}
                        photos={catPhotos}
                      />
                    )
                  })}

                  {/* Custom categories for this section */}
                  {getCustomCategoriesForSection(sectionLetter).map((customCat) => {
                    const catData = getCategoryData(customCat.code)
                    const catPhotos = getPhotosForCategory(customCat.code)

                    return (
                      <CategoryBlock
                        key={customCat.code}
                        code={customCat.code}
                        name={customCat.name}
                        comment={catData.comment}
                        rating={catData.rating}
                        photos={catPhotos}
                      />
                    )
                  })}
                </View>
              </View>

              {/* Footer */}
              <Image src="/footer.png" style={templateStyles.footerImage} fixed />
              <Text style={templateStyles.footerText} fixed>
                RICS Home Survey - Level {surveyLevel}
              </Text>
            </Page>
          )
        }

        // Standard page without background template
        return (
          <Page key={sectionLetter} size="A4" style={styles.page}>
            {/* Section header */}
            <SectionHeader
              sectionLetter={sectionLetter}
              surveyLevel={surveyLevel}
            />

            {/* Limitations box */}
            <LimitationsBox text={getLimitations(sectionLetter)} />

            {/* Categories */}
            {Object.entries(section.categories).map(([code, name]) => {
              const catData = getCategoryData(code)
              const catPhotos = getPhotosForCategory(code)

              return (
                <CategoryBlock
                  key={code}
                  code={code}
                  name={name}
                  comment={catData.comment}
                  rating={catData.rating}
                  photos={catPhotos}
                />
              )
            })}

            {/* Custom categories for this section */}
            {getCustomCategoriesForSection(sectionLetter).map((customCat) => {
              const catData = getCategoryData(customCat.code)
              const catPhotos = getPhotosForCategory(customCat.code)

              return (
                <CategoryBlock
                  key={customCat.code}
                  code={customCat.code}
                  name={customCat.name}
                  comment={catData.comment}
                  rating={catData.rating}
                  photos={catPhotos}
                />
              )
            })}

            {/* Footer */}
            <PageFooter surveyLevel={surveyLevel} />
          </Page>
        )
      })}

      {/* Standalone custom categories (after all regular sections) */}
      {standaloneCategories.length > 0 && (
        <Page size="A4" style={templateStyles.pageWithHeader}>
          {/* Fixed RICS header for all pages */}
          <Image
            src="/RICS.png?v=3"
            style={templateStyles.continuationLogo}
            fixed
          />

          {/* Spacer to push content below header */}
          <View style={{ height: 60 }} />

          {/* Custom Categories Title */}
          <View style={{ marginBottom: 15, paddingLeft: 40 }}>
            <Text style={{ fontSize: 14, fontWeight: 'bold', color: '#1e3a5f' }}>
              Additional Categories
            </Text>
          </View>

          {/* Rating legend */}
          <View style={templateStyles.ratingLegend}>
            <RatingCircle rating="1" size={20} />
            <RatingCircle rating="2" size={20} />
            <RatingCircle rating="3" size={20} />
            <RatingCircle rating="NI" size={20} />
          </View>

          {/* Standalone Categories */}
          <View style={templateStyles.categoryWrapper}>
            {standaloneCategories.map((customCat) => {
              const catData = getCategoryData(customCat.code)
              const catPhotos = getPhotosForCategory(customCat.code)

              return (
                <CategoryBlock
                  key={customCat.code}
                  code={customCat.code}
                  name={customCat.name}
                  comment={catData.comment}
                  rating={catData.rating}
                  photos={catPhotos}
                />
              )
            })}
          </View>

          {/* Footer */}
          <Image src="/footer.png" style={templateStyles.footerImage} fixed />
          <Text style={templateStyles.footerText} fixed>
            RICS Home Survey - Level {surveyLevel}
          </Text>
        </Page>
      )}
    </Document>
  )
}

export default SurveyDocument
